import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";

import {
  getCachedAndroidIcon,
  isAndroidNative,
  isAndroidPackageName,
  resolveAndroidIcon,
} from "../../lib/apps/androidAppIcon";
import { appIconUrl, resolveAppIconSlug, usesFullColorLocalIcon } from "../../lib/apps/appIcon";
import {
  brandfetchLogoIdentifierFromMapping,
  brandfetchIconUrl,
  resolveBrandfetchDomain,
  resolveIconSourceAfterLocalError,
  resolveInitialIconSource,
  type BrandfetchLogoIdentifier,
  type IconSource,
} from "../../lib/apps/brandfetchIcon";
import {
  appBrandfetchLookupKey,
  brandfetchSearchQueries,
  brandfetchSearchQuery,
  brandfetchSearchUrl,
  selectBrandfetchSearchResult,
  type BrandfetchSearchCandidate,
  type BrandfetchSearchSelection,
} from "../../lib/apps/brandfetchSearch";
import {
  normalizeCategoryLabel,
  resolveCategoryIcon,
} from "../../lib/apps/categoryIcons";
import { appStoreIconUrl } from "../../lib/apps/appStoreLookup";
import { useAppStoreMetadata } from "../../lib/apps/useAppStoreMetadata";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { brandfetchClientId } from "../../lib/env";

const DEFAULT_ICON_SIZE = 18;

type AppBrandfetchMapping = {
  lookupKey: string;
  brandfetchDomain?: string;
  brandfetchBrandId?: string;
  displayName?: string;
  qualityScore?: number;
  resolvedAt: number;
};

const getBrandfetchMappingRef = queryRef<{ lookupKey: string }, AppBrandfetchMapping | null>(
  "appBrandfetchMappings:getByKey",
);
const upsertBrandfetchMappingRef = mutationRef<{
  lookupKey: string;
  brandfetchDomain?: string;
  brandfetchBrandId?: string;
  displayName?: string;
  qualityScore?: number;
}, { id: string }>("appBrandfetchMappings:upsert");

const inFlightSearches = new Map<string, Promise<BrandfetchSearchSelection | null>>();
const missedSearchKeys = new Set<string>();

function logoIdentifierKey(identifier: BrandfetchLogoIdentifier | null): string {
  return identifier ? `${identifier.type}:${identifier.value}` : "";
}

function searchBrandfetch(
  lookupKey: string,
  queries: string[],
  clientId: string,
): Promise<BrandfetchSearchSelection | null> {
  const existing = inFlightSearches.get(lookupKey);
  if (existing) return existing;

  const request = (async () => {
    for (const query of queries) {
      try {
        const response = await fetch(brandfetchSearchUrl(query, clientId));
        if (!response.ok) continue;

        const json = await response.json();
        if (!Array.isArray(json)) continue;

        const selection = selectBrandfetchSearchResult(query, json as BrandfetchSearchCandidate[]);
        if (selection) return selection;
      } catch {
        continue;
      }
    }

    return null;
  })()
    .finally(() => {
      inFlightSearches.delete(lookupKey);
    });

  inFlightSearches.set(lookupKey, request);
  return request;
}

type Props = {
  displayName: string;
  appIdentifier?: string;
  category?: string;
  color: string;
  /** Fallback dot size (px). */
  size?: number;
  /** Resolved local/brandfetch icon size (px). */
  iconSize?: number;
  className?: string;
  /** When true, renders a category Lucide icon and skips all Brandfetch logic. */
  isCategory?: boolean;
};

export function AppIcon({
  displayName,
  appIdentifier,
  category,
  color,
  size = 12,
  iconSize = DEFAULT_ICON_SIZE,
  className = "",
  isCategory: isCategoryProp,
}: Props) {
  // Explicit prop wins; auto-detect when category equals displayName (standard iOS category entity).
  const isCategory = isCategoryProp ?? (!appIdentifier && !!category && displayName === category);

  const slug = resolveAppIconSlug({ displayName, appIdentifier, category });
  // Step 1 (App Store cache): a resolved App Store artwork URL wins over the
  // local SVG / Brandfetch chain. Hook runs unconditionally; it skips internally
  // for categories, websites, and non-iOS identifiers.
  const { metadata: appStoreMetadata } = useAppStoreMetadata({ displayName, appIdentifier, category });
  // Android resolves launcher icons natively; Apple Search / App Store is iOS-only.
  const androidPackage =
    !isCategory && isAndroidNative() && isAndroidPackageName(appIdentifier) ? appIdentifier! : null;
  const [androidIcon, setAndroidIcon] = useState<string | null>(() =>
    androidPackage ? getCachedAndroidIcon(androidPackage) ?? null : null,
  );

  useEffect(() => {
    if (!androidPackage) {
      setAndroidIcon(null);
      return;
    }
    const cached = getCachedAndroidIcon(androidPackage);
    if (cached !== undefined) {
      setAndroidIcon(cached);
      return;
    }
    let cancelled = false;
    void resolveAndroidIcon(androidPackage).then((uri) => {
      if (!cancelled) setAndroidIcon(uri);
    });
    return () => {
      cancelled = true;
    };
  }, [androidPackage]);

  const appStoreIcon = isCategory || isAndroidNative() ? null : appStoreIconUrl(appStoreMetadata);
  const [appStoreFailed, setAppStoreFailed] = useState(false);
  const canUseAppStoreIcon = Boolean(appStoreIcon && !appStoreFailed);
  // Gate all Brandfetch-related derivations on !isCategory so they never fire for categories.
  const domain = isCategory ? null : resolveBrandfetchDomain({ displayName, appIdentifier, category });
  const lookupKey = isCategory ? null : appBrandfetchLookupKey({ displayName, appIdentifier, category });
  const searchQuery = isCategory ? null : brandfetchSearchQuery({ displayName, appIdentifier, category });
  const searchQueries = isCategory ? [] : brandfetchSearchQueries({ displayName, appIdentifier, category });
  const cachedMapping = useQuery(
    getBrandfetchMappingRef,
    !slug && !domain && lookupKey ? { lookupKey } : "skip",
  );
  const upsertMapping = useMutation(upsertBrandfetchMappingRef);
  const [isOnline, setIsOnline] = useState(
    () => typeof navigator !== "undefined" && navigator.onLine,
  );
  const [searchedMapping, setSearchedMapping] = useState<{
    lookupKey: string;
    brandfetchDomain?: string;
    brandfetchBrandId?: string;
  } | null>(null);
  const [failedLogoKey, setFailedLogoKey] = useState("");
  const cachedIdentifier = brandfetchLogoIdentifierFromMapping(cachedMapping ?? {});
  const searchedIdentifier = searchedMapping?.lookupKey === lookupKey
    ? brandfetchLogoIdentifierFromMapping(searchedMapping)
    : null;
  const brandfetchIdentifier = domain
    ? { type: "domain" as const, value: domain }
    : (searchedIdentifier ?? cachedIdentifier);
  const currentLogoKey = logoIdentifierKey(brandfetchIdentifier);
  const canUseBrandfetchLogo = Boolean(
    isOnline &&
      brandfetchClientId &&
      brandfetchIdentifier &&
      currentLogoKey !== failedLogoKey,
  );
  const [source, setSource] = useState<IconSource>(() =>
    resolveInitialIconSource({
      slug,
      domain,
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
      clientId: brandfetchClientId,
    }),
  );

  useEffect(() => {
    setSource(
      canUseAppStoreIcon
        ? "appstore"
        : slug
          ? "local"
          : canUseBrandfetchLogo
            ? "brandfetch"
            : "dot",
    );
  }, [canUseAppStoreIcon, slug, canUseBrandfetchLogo]);

  useEffect(() => {
    setAppStoreFailed(false);
  }, [appStoreIcon]);

  useEffect(() => {
    setFailedLogoKey("");
  }, [lookupKey, domain]);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!isOnline && source === "brandfetch") {
      setSource("dot");
    }
  }, [isOnline, source]);

  useEffect(() => {
    if (
      slug ||
      domain ||
      !lookupKey ||
      !searchQuery ||
      searchQueries.length === 0 ||
      !brandfetchClientId ||
      !isOnline ||
      cachedMapping === undefined ||
      cachedMapping ||
      searchedMapping?.lookupKey === lookupKey ||
      missedSearchKeys.has(lookupKey)
    ) {
      return;
    }

    let cancelled = false;
    void searchBrandfetch(lookupKey, searchQueries, brandfetchClientId).then((selection) => {
      if (cancelled) return;
      if (!selection) {
        missedSearchKeys.add(lookupKey);
        return;
      }

      const nextMapping = {
        lookupKey,
        brandfetchDomain: selection.brandfetchDomain,
        brandfetchBrandId: selection.brandfetchBrandId,
      };
      setSearchedMapping(nextMapping);
      void upsertMapping({
        ...nextMapping,
        displayName: selection.displayName,
        qualityScore: selection.qualityScore,
      }).catch(() => undefined);
    });

    return () => {
      cancelled = true;
    };
  }, [
    slug,
    domain,
    lookupKey,
    searchQuery,
    searchQueries,
    isOnline,
    cachedMapping,
    searchedMapping,
    upsertMapping,
  ]);

  const dotClassName = ["shrink-0 rounded-full", className].filter(Boolean).join(" ");
  const localIconClassName = [
    "shrink-0 rounded-md object-contain brightness-0 invert",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const brandfetchIconClassName = ["shrink-0 rounded-md object-contain", className]
    .filter(Boolean)
    .join(" ");

  // Category entities: render a Lucide icon (or dot for unknown) — never Brandfetch.
  if (isCategory) {
    const canonicalKey = normalizeCategoryLabel(category ?? displayName);
    if (canonicalKey) {
      const Icon = resolveCategoryIcon(canonicalKey);
      return (
        <Icon
          size={iconSize}
          className={["shrink-0", className].filter(Boolean).join(" ")}
          style={{ color }}
          aria-hidden
        />
      );
    }
    return (
      <span
        className={dotClassName}
        style={{ width: size, height: size, backgroundColor: color }}
        aria-hidden
      />
    );
  }

  // Android: a natively resolved launcher icon wins over the local/Brandfetch chain.
  // When it is null (unresolved or app uninstalled), fall through to that chain.
  if (androidIcon) {
    return (
      <img
        src={androidIcon}
        alt=""
        aria-hidden
        className={brandfetchIconClassName}
        style={{ width: iconSize, height: iconSize }}
      />
    );
  }

  if (source === "dot") {
    return (
      <span
        className={dotClassName}
        style={{ width: size, height: size, backgroundColor: color }}
        aria-hidden
      />
    );
  }

  if (source === "appstore" && appStoreIcon) {
    return (
      <img
        src={appStoreIcon}
        alt=""
        aria-hidden
        className={brandfetchIconClassName}
        style={{ width: iconSize, height: iconSize }}
        onError={() => {
          setAppStoreFailed(true);
          setSource(slug ? "local" : canUseBrandfetchLogo ? "brandfetch" : "dot");
        }}
      />
    );
  }

  if (source === "local" && slug) {
    return (
      <img
        src={appIconUrl(slug)}
        alt=""
        aria-hidden
        className={usesFullColorLocalIcon(slug) ? brandfetchIconClassName : localIconClassName}
        style={{ width: iconSize, height: iconSize }}
        onError={() =>
          setSource(
            resolveIconSourceAfterLocalError({
              domain,
              isOnline,
              clientId: brandfetchClientId,
            }),
          )
        }
      />
    );
  }

  if (source === "brandfetch" && brandfetchIdentifier && brandfetchClientId) {
    return (
      <img
        src={brandfetchIconUrl(brandfetchIdentifier, iconSize, brandfetchClientId)}
        alt=""
        aria-hidden
        className={brandfetchIconClassName}
        style={{ width: iconSize, height: iconSize }}
        onError={() => {
          setFailedLogoKey(currentLogoKey);
          setSource("dot");
        }}
      />
    );
  }

  return (
    <span
      className={dotClassName}
      style={{ width: size, height: size, backgroundColor: color }}
      aria-hidden
    />
  );
}
